import styles from "./CobraCensus.module.css";
import { useEffect, useState } from "react";
import { useCensusAndCurrentInformationContext } from "pages/CensusAndCurrentInformation/CensusAndCurrentInformation";
import {
  trackPageViewUserStep,
  trackFormError,
  trackFormSubmission,
} from "utils/analytics/trackEvent";
import Alert from "uicomponents/Alert";
import CensusDrawer from "uicomponents/CensusDrawer";
import CensusTable from "uicomponents/CensusTable";
import {
  useDeleteCobraParticipantMutation,
  useUpdateStepMutation,
} from "types/generated/operations";
import { Modal } from "@justworkshr/milo-components";

import { CobraCensusType } from "types";
import { DependentType, EmployeeType } from "uicomponents/CensusTable/types";

import { mapBEStepstoMatchFE } from "pages/CensusAndCurrentInformation/utils/helper";
import {
  STEPS_WITH_COBRA,
  STEPS_WITHOUT_COBRA,
} from "pages/CensusAndCurrentInformation/utils/constants";

import Stepper from "uicomponents/Stepper";
import { errorMessageMap, getStepNumberFromURL } from "utils/helper";
import Error from "uicomponents/Error";

interface ICobraCensusProps {
  data: CobraCensusType;
  refetchData: () => void;
}

interface IDrawerState {
  status: string;
  initialData?: DependentType | EmployeeType;
}

const CobraCensus = (props: ICobraCensusProps): JSX.Element => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageViewUserStep(5, "current empoloyee information");
  }, []);

  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { data } = props;
  const existingCensuses = data?.cobraCensuses || [];

  const [drawerState, setDrawerState] = useState<IDrawerState>({
    status: "closed",
    initialData: undefined,
  });

  const hasCobra = data?.healthInsuranceInformation?.cobraUsage;

  const { previousStep, nextStep, stepperNextStep } =
    useCensusAndCurrentInformationContext();
  const [deleteCobraParticipant, { loading: deleteCobraParticipantLoading }] =
    useDeleteCobraParticipantMutation();
  const [updateStep, { loading: updateStepLoading }] = useUpdateStepMutation();

  const STEPS = hasCobra ? STEPS_WITH_COBRA : STEPS_WITHOUT_COBRA;

  const applicationStep = data?.applications?.[0]?.step;
  const currentStep = getStepNumberFromURL();
  const isInValidStep = applicationStep && currentStep > applicationStep;

  if (isInValidStep) {
    return <Error />;
  }

  const handleSaveAndContinue = async () => {
    try {
      if (data?.applications?.[0]?.step === currentStep) {
        await updateStep({
          variables: {
            uuid: data?.applications?.[0].uuid,
            step: data?.applications?.[0]?.step + 1,
          },
        });
        trackFormSubmission(7, "CensusAndCurrentInfo");
      }
      nextStep();
    } catch (error: any) {
      const errorMessage: string = error?.message;
      errorMessageMap[errorMessage] &&
        setErrorMessage(errorMessageMap[errorMessage]);
      trackFormError(error, errorMessage);
      setIsError(true);
      return;
    }
  };

  const handleDelete = async (uuid: string) => {
    try {
      if (!uuid) {
        return true;
      }
      await deleteCobraParticipant({
        variables: {
          uuid,
        },
      });
      props.refetchData();
      return true;
    } catch (error: any) {
      const errorMessage: string = error?.message;
      setErrorMessage(errorMessageMap[errorMessage] || "Please try again");
      trackFormError(error, errorMessage);
      setIsError(true);
      return false;
    }
  };

  const openDrawer = (initialData?: DependentType | EmployeeType) => () => {
    setDrawerState({
      status: "animating",
      initialData,
    });
    setTimeout(() => {
      setDrawerState({
        initialData,
        status: "open",
      });
    }, 10);
  };

  const closeDrawer = (refetch?: boolean) => {
    setDrawerState({
      ...drawerState,
      status: "animating",
    });
    setTimeout(() => {
      setDrawerState({
        status: "closed",
      });
    }, 500);
    if (refetch) props.refetchData();
  };

  return (
    <>
      <Modal
        isOpen={drawerState.status !== "closed"}
        onClose={() => closeDrawer()}
      >
        {drawerState.status !== "closed" && (
          <CensusDrawer
            isOpen={drawerState.status === "open"}
            initialData={drawerState.initialData}
            closeDrawer={closeDrawer}
            isCobra
          />
        )}
      </Modal>
      <div className={styles.censusContainer}>
        <Stepper
          steps={STEPS}
          currentStep={mapBEStepstoMatchFE(getStepNumberFromURL())}
          status={mapBEStepstoMatchFE(data?.applications?.[0].step || 0) || 8}
          onClick={(stepNumber) => stepperNextStep(stepNumber)}
        />
        <div>
          <h3>COBRA participants</h3>
          <div className={styles.description}>
            We also need all former employees and their dependents that are
            currently on COBRA.
          </div>

          <CensusTable
            censusList={existingCensuses}
            handleBackClick={previousStep}
            handleDelete={handleDelete}
            offerHealthInsurance={
              !!props.data?.healthInsuranceInformation
                ?.currentlyOfferHealthInsurance
            }
            isLoading={deleteCobraParticipantLoading || updateStepLoading}
            openDrawer={openDrawer}
            handleSaveAndContinue={handleSaveAndContinue}
            isCobra
          />

          {isError && <Alert message={errorMessage} />}
        </div>
      </div>
    </>
  );
};

export default CobraCensus;
